import { useEffect } from 'react';

import { useMutation } from '@tanstack/react-query';
import { TypeCreateWorkApplicationsRequest } from '~/api/workApplications';

import { axiosInstance } from '~/services/fetchServiceV2';
import { useLoadingStore } from '~/store';
import { formatDate } from '~/utils';

export type AdditionalSubmitResult =
  | { type: 'success' }
  | { type: 'skipped' }
  | { type: 'allClosed'; closedDates: string }
  | { type: 'partialClosed'; closedDates: string }
  | { type: 'error' };

export type AdditionalSubmitSelection = {
  workDate: string;
  firstRecruitNoticeId: string;
  secondRecruitNoticeId?: string | null;
};

const formatClosedDates = (
  selections: AdditionalSubmitSelection[],
  settledResults: PromiseSettledResult<unknown>[]
) => {
  const sortedDates = selections
    .filter((_, index) => settledResults[index].status === 'rejected')
    .map((selection) => selection.workDate)
    .sort();

  let previousYearMonth: string | null = null;
  return sortedDates
    .map((date) => {
      const yearMonth = formatDate({ value: date, format: 'YYYY-M' });
      const format = yearMonth === previousYearMonth ? 'D일' : 'M월 D일';
      previousYearMonth = yearMonth;
      return formatDate({ value: date, format });
    })
    .join(', ');
};

/**
 * @description 추가 지원(추천 날짜) 제출 훅
 */
export const useSubmitAdditionalWorkApply = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      params,
      selections,
    }: {
      params: TypeCreateWorkApplicationsRequest;
      selections: AdditionalSubmitSelection[];
    }): Promise<AdditionalSubmitResult> => {
      const settledResults = await Promise.allSettled(
        selections.map((selection) =>
          axiosInstance.post('/work-applications', {
            ...params,
            workDate: selection.workDate,
            firstRecruitNoticeId: selection.firstRecruitNoticeId,
            secondRecruitNoticeId: selection.secondRecruitNoticeId ?? null,
            privacyPolicyAgreed: true,
          })
        )
      );
      const succeededCount = settledResults.filter((r) => r.status === 'fulfilled').length;
      const failedCount = settledResults.length - succeededCount;

      if (failedCount === 0) return { type: 'success' }; // 모두 성공 시

      const hasNonClosedError = settledResults.some(
        (r) => r.status === 'rejected' && r.reason?.response?.data?.code !== 'RN002'
      );
      if (hasNonClosedError) return { type: 'error' };

      const closedDates = formatClosedDates(selections, settledResults);

      if (succeededCount === 0) {
        return { type: 'allClosed', closedDates };
      } else {
        return { type: 'partialClosed', closedDates };
      }
    },
  });

  const setLoading = useLoadingStore((state) => state.actions.setLoading);

  useEffect(() => {
    setLoading({ show: isPending, zIndex: 99999 });

    return () => {
      setLoading({ show: false });
    };
  }, [isPending]);

  return { mutateAsync, isPending };
};
